import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Car, ArrowLeft, QrCode, Share2, Star, Zap, MapPin, Clock, Route } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { chatService } from "@/services/chatService";
import { PaymentSummary } from "./PaymentSummary";
import { PreferencesDisplay } from "./PreferencesDisplay";
import { ChaloAIIcon } from "@/components/ui/ChaloAIIcon";

const CAR_IMAGE = "https://static-01.daraz.pk/p/9ae924b4559b735b7f2af2e2da1b8dc1.jpg";

interface StopLocation {
  id: string;
  name: string;
  coordinates: [number, number];
  type: 'pickup' | 'enroute' | 'dropoff';
}

interface RideData {
  from: string;
  to: string;
  pickup: string;
  drop: string;
  date: string;
  time: string;
  car: string;
  price: number;
  preferences: string[];
  seats: number;
  seatPrefs: string;
  baggage: string;
  note: string;
  stopLocations?: StopLocation[];
  preferencesCharges?: { [key: string]: number };
}

interface QRCodeProps {
  value: string;
  size?: number;
}

const QRCode: React.FC<QRCodeProps> = ({ value, size = 200 }) => {
  return (
    <div className="flex items-center justify-center bg-white p-4 rounded-lg shadow-lg">
      <div 
        className="bg-black"
        style={{
          width: size,
          height: size,
          background: `url("https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}")`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat'
        }}
      />
    </div>
  );
};

export const OfferRideReview: React.FC<{
  rideData: RideData;
  onEdit: (field: string) => void;
  onBack: () => void;
}> = ({ rideData, onEdit, onBack }) => {
  const [selectedPayment, setSelectedPayment] = useState("chalo-wallet");
  const [isPublished, setIsPublished] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const [showStops, setShowStops] = useState(false);

  const hasStopLocations = rideData.stopLocations && rideData.stopLocations.length > 0;

  // Mock distance and time calculation
  const mockDistance = "186 km";
  const mockTravelTime = "3h 45m";

  React.useEffect(() => {
    const getAIAssistance = async () => {
      try {
        const response = await chatService.getAIHelp(
          `Analyze ride demand for route ${rideData.from} to ${rideData.to}`,
          `Route: ${rideData.from} to ${rideData.to}, Date: ${rideData.date}, Time: ${rideData.time}`
        );
        if (response.success) {
          setAiMessage(response.message);
        } else {
          setAiMessage("There are 78% of travellers waiting to hop on this route. Rideful chances are 89%. Encourage to publish to ride!");
        }
      } catch (error) {
        setAiMessage("There are 78% of travellers waiting to hop on this route. Rideful chances are 89%. Encourage to publish to ride!");
      }
    };

    getAIAssistance();
  }, [rideData]);

  const handlePublish = () => {
    setIsPublished(true);
  };

  const handleBackToChat = () => {
    onBack();
  };

  const shareUrl = `https://chalo.app/ride/${rideData.from}-${rideData.to}-${Date.now()}`;

  if (isPublished) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-white sticky top-0 z-10">
          <Button variant="ghost" onClick={handleBackToChat} size="sm" className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h2 className="text-lg font-semibold text-green-600">Ride Published!</h2>
          <div className="w-10"></div>
        </div>

        <div className="flex-1 p-4 flex flex-col items-center justify-center text-center space-y-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <Car className="w-8 h-8 text-green-600" />
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Your ride is now live!</h3>
            <p className="text-gray-600">Share this QR code to let others join your ride</p>
          </div>

          <QRCode value={shareUrl} size={200} />

          <div className="space-y-3 w-full max-w-sm">
            <Button 
              className="w-full flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              onClick={() => navigator.share({ 
                title: `Join my ride from ${rideData.from} to ${rideData.to}`,
                url: shareUrl 
              })}
            >
              <Share2 className="w-4 h-4" />
              Share Ride
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleBackToChat}
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (showStops && hasStopLocations) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex flex-col">
        <div className="flex items-center justify-between p-4 border-b bg-white sticky top-0 z-10">
          <Button variant="ghost" onClick={() => setShowStops(false)} size="sm" className="p-2">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h2 className="text-lg font-semibold text-gray-800">Ride Stops</h2>
          <div className="w-10"></div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <Card>
            <CardContent className="p-4">
              <div className="space-y-4">
                {rideData.stopLocations!.map((stop, i) => (
                  <div key={stop.id} className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className={`w-3 h-3 rounded-full ${
                      stop.type === 'pickup' ? 'bg-green-500' : 
                      stop.type === 'enroute' ? 'bg-yellow-500' : 'bg-red-500'
                    }`} />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{stop.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{stop.type} stop</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-white sticky top-0 z-10">
        <Button variant="ghost" onClick={onBack} size="sm" className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-lg font-semibold text-[#22C55E]">Review Your Ride</h2>
        <div className="w-10"></div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Stop Locations Missing Warning */}
        {!hasStopLocations && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-800 mb-1">Complete Your Ride Setup</h4>
                  <p className="text-sm text-orange-700 mb-3">
                    Please add ride stop locations (pickup, en-route, and drop-off points) to help travelers find convenient meeting spots and increase your bookings.
                  </p>
                  <Button
                    size="sm"
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => onEdit('stops')}
                  >
                    Add Stop Locations
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Chalo AI Assistance Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <ChaloAIIcon />
              <div className="flex-1">
                <h4 className="font-semibold text-blue-800 mb-1">Chalo AI Assistance</h4>
                <p className="text-sm text-blue-700">{aiMessage}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Chalo Offer Badge */}
        <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Star className="w-6 h-6 text-orange-500" />
              <div className="flex-1">
                <Badge className="bg-orange-500 text-white mb-2">Chalo Offer</Badge>
                <p className="text-sm font-medium text-gray-800">
                  This ride gets you <span className="font-bold text-orange-600">250 Chalo Miles</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  You are <span className="font-semibold">5000 Chalo Miles</span> away from Chalo Premium membership!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ride Details */}
        <Card>
          <CardContent className="p-4">
            <div className="flex gap-4 mb-4">
              <img
                src={CAR_IMAGE}
                alt="Car"
                className="w-24 h-16 object-cover rounded-lg border flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Car className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-sm truncate">{rideData.car}</span>
                </div>
                <div className="text-xs text-gray-600">Seats: {rideData.seats}</div>
              </div>
            </div>

            {/* Route Info with Distance and Time */}
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div>
                  <div className="text-gray-500 text-xs">From</div>
                  <div className="font-medium">{rideData.from}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs">To</div>
                  <div className="font-medium">{rideData.to}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 text-xs text-gray-600 mb-3">
                <div className="flex items-center gap-1">
                  <Route className="w-3 h-3" />
                  <span>{mockDistance}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span>{mockTravelTime}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-gray-500 text-xs">Date</div>
                  <div className="font-medium">{rideData.date}</div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs">Time</div>
                  <div className="font-medium">{rideData.time}</div>
                </div>
              </div>
            </div>

            {/* Stop Locations */}
            {hasStopLocations && (
              <div className="mb-3 pb-3 border-b">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-gray-500">Ride Stops ({rideData.stopLocations!.length})</div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs h-6 px-2"
                    onClick={() => setShowStops(true)}
                  >
                    View All
                  </Button>
                </div>
                <div className="space-y-1">
                  {rideData.stopLocations!.slice(0, 2).map((stop, i) => (
                    <div key={stop.id} className="flex items-center gap-2 text-xs">
                      <div className={`w-2 h-2 rounded-full ${
                        stop.type === 'pickup' ? 'bg-green-500' : 
                        stop.type === 'enroute' ? 'bg-yellow-500' : 'bg-red-500'
                      }`} />
                      <span className="capitalize text-gray-600">{stop.type}:</span>
                      <span>{stop.name}</span>
                    </div>
                  ))}
                  {rideData.stopLocations!.length > 2 && (
                    <div className="text-xs text-gray-500">
                      +{rideData.stopLocations!.length - 2} more stops
                    </div>
                  )}
                </div>
              </div>
            )}

            <PreferencesDisplay 
              preferences={rideData.preferences}
              preferencesCharges={rideData.preferencesCharges}
              showCharges={true}
            />
          </CardContent>
        </Card>

        {/* Payment Accepted Section */}
        <Card>
          <CardContent className="p-4">
            <h4 className="font-semibold text-gray-800 mb-3">Payment Accepted</h4>
            <RadioGroup value={selectedPayment} onValueChange={setSelectedPayment} className="space-y-3">
              <div className="flex items-center space-x-2 p-3 border rounded-lg">
                <RadioGroupItem value="cash" id="cash" />
                <label htmlFor="cash" className="flex-1 cursor-pointer">
                  <div className="font-medium">Cash</div>
                  <div className="text-xs text-gray-500">Pay directly to driver</div>
                </label>
              </div>
              
              <div className="flex items-center space-x-2 p-3 border rounded-lg bg-blue-50 border-blue-200">
                <RadioGroupItem value="chalo-wallet" id="chalo-wallet" />
                <label htmlFor="chalo-wallet" className="flex-1 cursor-pointer">
                  <div className="font-medium text-blue-800">Chalo Wallet</div>
                  <div className="text-xs text-blue-600">Recommended • Earn miles</div>
                </label>
                <Badge className="bg-blue-500 text-white text-xs">+10 Miles</Badge>
              </div>
              
              <div className="flex items-center space-x-2 p-3 border rounded-lg">
                <RadioGroupItem value="upi" id="upi" />
                <label htmlFor="upi" className="flex-1 cursor-pointer">
                  <div className="font-medium">UPI</div>
                  <div className="text-xs text-gray-500">Pay via UPI apps</div>
                </label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Payment Summary */}
        <PaymentSummary 
          basePrice={rideData.price}
          preferencesCharges={rideData.preferencesCharges}
          showRewards={true}
        />
      </div>

      <div className="p-4 border-t bg-white">
        <Button 
          onClick={handlePublish}
          className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white py-3 text-base font-semibold"
        >
          Publish Ride
        </Button>
      </div>
    </div>
  );
};
